import {
  BREAK_GLASS_NAMES,
  DOMAIN_WIDE_GROUPS,
  PENDING_REVIEW_STATUS,
  REVOKE_REQUESTED_STATUS,
  type PillTone,
  type RiskLevel,
} from "../constants/pagConstants";
import type { PagAccountRow, PagRiskAccount, PagSourceSystemFilter } from "../services/pagApi";

export const hasOpenReviewRequest = (reviewStatus: string | null): boolean =>
  reviewStatus === PENDING_REVIEW_STATUS || reviewStatus === REVOKE_REQUESTED_STATUS;

export const formatInventoryDate = (value: string | null | undefined) => {
  if (!value) return null;
  /* The API sends plain dates and ISO datetimes; only the date part shows. */
  const [year, month, day] = value.split("T")[0].split("-");
  return year && month && day ? `${day}/${month}/${year}` : value;
};

function parseEpochTimestamp(value: number): number {
  return value < 1e11 ? value * 1000 : value;
}

interface DateParts {
  year: number;
  month: number;
  day: number;
  hour?: number;
  min?: number;
  sec?: number;
}

function constructDateTime(parts: DateParts): number {
  return new Date(
    parts.year,
    parts.month,
    parts.day,
    parts.hour ?? 23,
    parts.min ?? 59,
    parts.sec ?? 59,
  ).getTime();
}

function parseDdmmyyyy(str: string): number | null {
  const match =
    /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:[T\s](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?/.exec(
      str,
    );
  if (!match) return null;
  return constructDateTime({
    day: parseInt(match[1], 10),
    month: parseInt(match[2], 10) - 1,
    year: parseInt(match[3], 10),
    hour: match[4] ? parseInt(match[4], 10) : undefined,
    min: match[5] ? parseInt(match[5], 10) : undefined,
    sec: match[6] ? parseInt(match[6], 10) : undefined,
  });
}

function parseYyyymmdd(str: string): number | null {
  const match =
    /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})(?:[T\s](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?/.exec(
      str,
    );
  if (!match) return null;
  return constructDateTime({
    year: parseInt(match[1], 10),
    month: parseInt(match[2], 10) - 1,
    day: parseInt(match[3], 10),
    hour: match[4] ? parseInt(match[4], 10) : undefined,
    min: match[5] ? parseInt(match[5], 10) : undefined,
    sec: match[6] ? parseInt(match[6], 10) : undefined,
  });
}

function parseDateStringToTimestamp(str: string): number | null {
  const trimmed = str.trim();
  if (!trimmed) return null;

  const ddmmyyyyTime = parseDdmmyyyy(trimmed);
  if (ddmmyyyyTime !== null) return ddmmyyyyTime;

  const yyyymmddTime = parseYyyymmdd(trimmed);
  if (yyyymmddTime !== null) return yyyymmddTime;

  const parsed = Date.parse(trimmed);
  return Number.isNaN(parsed) ? null : parsed;
}

function resolveTargetTimestamp(value: string | number): number | null {
  if (typeof value === "number") {
    return parseEpochTimestamp(value);
  }
  return parseDateStringToTimestamp(String(value));
}

/** Robustly checks if a date string/number has passed today's date (overdue). */
export const isDateOverdue = (value: string | number | null | undefined): boolean => {
  if (value === null || value === undefined || value === "") return false;
  try {
    const timestamp = resolveTargetTimestamp(value);
    if (timestamp === null) return false;
    return timestamp < Date.now();
  } catch {
    return false;
  }
};

/** Retrieves the dueDate for an account from row fields or stored cache. */
export const getAccountDueDate = (row: {
  accountName?: string;
  adUserId?: number | string;
  dueDate?: unknown;
  due_date?: unknown;
  reviewDate?: unknown;
  review_date?: unknown;
  targetDate?: unknown;
  target_date?: unknown;
}): string | number | null | undefined => {
  const r = row as Record<string, unknown>;
  const val = r.dueDate ?? r.due_date ?? r.reviewDate ?? r.review_date ?? r.targetDate ?? r.target_date;
  if (val !== undefined && val !== null) {
    return val as string | number;
  }
  try {
    const stored = JSON.parse(localStorage.getItem("pag_account_due_dates") || "{}");
    if (row.accountName && stored[row.accountName]) return stored[row.accountName];
    if (row.adUserId && stored[String(row.adUserId)]) return stored[String(row.adUserId)];
  } catch {}
  return undefined;
};

/** Checks if a privileged account row is overdue. */
export const isAccountOverdue = (row: {
  reviewStatus?: string | null;
  isOverdue?: boolean;
  overdue?: boolean;
  accountName?: string;
  adUserId?: number | string;
  dueDate?: unknown;
  due_date?: unknown;
  reviewDate?: unknown;
  review_date?: unknown;
  targetDate?: unknown;
  target_date?: unknown;
}): boolean => {
  const r = row as Record<string, unknown>;
  if (row.isOverdue || r.overdue || row.reviewStatus === "Overdue") return true;

  const dueDate = getAccountDueDate(row);
  if (dueDate) {
    return isDateOverdue(dueDate) && row.reviewStatus !== "Reviewed" && row.reviewStatus !== "Revoked";
  }

  return false;
};

export const OVERDUE_ROW_CLASS =
  "!bg-red-500/[0.07] dark:!bg-red-500/[0.08] hover:!bg-red-500/[0.12] dark:hover:!bg-red-500/[0.12] transition-colors";

/** The account list sends epoch seconds (or ms); 0 means "never". Formats as "DD/MM/YYYY, HH:mm" */
export const formatEpochDate = (value: number | null): string | null => {
  if (!value || value <= 0) return null;
  const ms = value > 1e11 ? value : value * 1000;
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, "0");
  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear();
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  return `${day}/${month}/${year}, ${hours}:${minutes}`;
};

/** PAM status arrives as free text ("Vaulted"), so the tone is matched loosely. */
export const pamStatusTone = (status: string): PillTone => {
  const value = status.toLowerCase();
  if (value.includes("not") || value.includes("un")) return "red";
  if (value.includes("cover") || value.includes("vault")) return "emerald";
  return "slate";
};

/** Blend a hex colour toward black (ratio < 0) or white (ratio > 0). */
export const shadeColor = (hex: string, ratio: number): string => {
  let raw = String(hex ?? "").replace("#", "").trim();
  if (raw.length === 3) raw = raw.split("").map((c) => c + c).join("");
  if (!/^[0-9a-fA-F]{6}$/.test(raw)) return hex;
  const target = ratio < 0 ? 0 : 255;
  const amount = Math.min(1, Math.abs(ratio));
  const channel = (offset: number) => {
    const base = (parseInt(raw, 16) >> offset) & 255;
    return Math.max(0, Math.min(255, Math.round(base + (target - base) * amount)));
  };
  const toHex = (v: number) => v.toString(16).padStart(2, "0");
  return `#${toHex(channel(16))}${toHex(channel(8))}${toHex(channel(0))}`;
};

function tokenizeCategoryLabel(raw: string): string[] {
  let tokens = raw.split(/\s+/).filter(Boolean);
  if (tokens.length === 1 && (raw.includes("-") || raw.includes("_"))) {
    tokens = raw.split(/(?<=[-_])/).filter(Boolean);
  }
  return tokens;
}

function handleSingleTokenOverflow(str: string, maxChars: number): string[] {
  if (str.length <= maxChars) return [str];
  if (str.length <= maxChars * 2) {
    return [str.slice(0, maxChars), str.slice(maxChars, maxChars * 2)];
  }
  return [str.slice(0, maxChars), `${str.slice(maxChars, maxChars * 2 - 1)}…`];
}

function appendToken(currentLine: string, token: string): string {
  if (!currentLine) return token;
  if (currentLine.endsWith("-") || currentLine.endsWith("_")) {
    return `${currentLine}${token}`;
  }
  return `${currentLine} ${token}`;
}

function buildCategoryLine(
  tokens: string[],
  startIndex: number,
  maxChars: number,
): { line: string; nextIndex: number } {
  let line = "";
  let i = startIndex;

  while (i < tokens.length) {
    const token = tokens[i];
    const candidate = appendToken(line, token);

    if (!line) {
      line = token.length > maxChars ? `${token.slice(0, maxChars - 1)}…` : token;
      i++;
    } else if (candidate.length <= maxChars) {
      line = candidate;
      i++;
    } else {
      break;
    }
  }

  return { line, nextIndex: i };
}

function finalizeSecondLine(line2: string, hasMore: boolean, maxChars: number): string {
  if (hasMore) {
    if (line2.length >= maxChars) {
      return `${line2.slice(0, maxChars - 1).trimEnd()}…`;
    }
    return `${line2}…`;
  }
  if (line2.length > maxChars) {
    return `${line2.slice(0, maxChars - 1).trimEnd()}…`;
  }
  return line2;
}

/** Wrap a chart category onto at most two straight (never rotated) lines with strict width limits. */
export const wrapCategoryLabel = (text: string, maxChars = 14): string[] => {
  const raw = String(text ?? "").trim();
  if (!raw) return [];
  if (raw.length <= maxChars) return [raw];

  const tokens = tokenizeCategoryLabel(raw);
  if (tokens.length === 1) {
    return handleSingleTokenOverflow(tokens[0], maxChars);
  }

  const { line: line1, nextIndex: afterLine1 } = buildCategoryLine(tokens, 0, maxChars);
  if (afterLine1 >= tokens.length) {
    return [line1];
  }

  const { line: line2, nextIndex: afterLine2 } = buildCategoryLine(tokens, afterLine1, maxChars);
  const finalLine2 = finalizeSecondLine(line2, afterLine2 < tokens.length, maxChars);

  return [line1, finalLine2];
};

/* ── Source system filter ───────────────────────────────────────────── */

/** Source System is the system itself ("Active Directory"). */
export const sourceSystemOptions = (systems: PagSourceSystemFilter[]) =>
  systems.map((system: any) => ({
    value: String(system.sourceSystem ?? system.name ?? system.displayName ?? ""),
    label: system.displayName ?? system.sourceSystem ?? system.name ?? "System",
  }));

export const resourceOptions = (
  systems: PagSourceSystemFilter[],
  selectedSystems: string[],
) =>
  systems
    .filter(
      (system: any) =>
        selectedSystems.length === 0 ||
        selectedSystems.includes(String(system.sourceSystem ?? system.name ?? system.displayName ?? "")),
    )
    .flatMap((system: any) =>
      (system.instances ?? system.resources ?? []).map((instance: any) => {
        const id =
          instance.sourceSystemId ??
          instance.id ??
          instance.systemId ??
          instance.instanceId;
        return {
          value: String(id),
          label: instance.name ?? instance.instanceName ?? instance.displayName ?? `System ${id}`,
        };
      }),
    );

/** Returns all resource values belonging to the given source systems. */
export const getResourceValuesForSystems = (
  systems: PagSourceSystemFilter[],
  selectedSystems: string[],
): string[] => {
  if (!selectedSystems.length) return [];
  return resourceOptions(systems, selectedSystems).map((opt) => opt.value);
};

/** Map selected row IDs to unique non-empty account names. */
export const extractSelectedAccountNames = <
  T extends { reviewId?: number; accountName?: string | null },
>(
  rows: T[],
  selectedIds: number[],
): string[] =>
  Array.from(
    new Set(
      rows
        .filter((row) => row.reviewId !== undefined && selectedIds.includes(row.reviewId))
        .map((row) => row.accountName)
        .filter((name): name is string => Boolean(name)),
    ),
  );


export const pagErrorMessage = (
  err: unknown,
  fallback = "Please try again in a moment.",
): string => {
  const raw = err instanceof Error ? err.message.trim() : "";
  if (!raw) return fallback;
  if (/^[[{<]/.test(raw)) return fallback;
  /* A stack trace or a wall of text is no more use than the fallback. */
  if (raw.length > 200 || raw.includes(String.fromCharCode(10))) return fallback;
  return raw;
};


export const standardRuleRisk = (account: PagRiskAccount): RiskLevel => {
  const name = (account.samAccountName ?? "").toLowerCase();
  if (BREAK_GLASS_NAMES.some((needle) => name.includes(needle))) return "CRITICAL";

  const groups = (account.privilegedGroups ?? []).map((g) => g.toLowerCase());
  if (groups.some((group) => DOMAIN_WIDE_GROUPS.includes(group))) return "HIGH";
  if (groups.length > 0) return "MEDIUM";
  return "LOW";
};

export interface NormalizedPaginatedResult<T> {
  rows: T[];
  total: number;
}

/**
 * Normalizes paginated API responses that may arrive as:
 * - Spring Page: `{ content: T[], totalElements?: number }`
 * - Envelope object: `{ data: T[], total?: number }`
 * - Raw array: `T[]`
 *
 * Safely inspects the shape without requiring `as any` casts.
 */
export function normalizePaginatedResponse<T>(raw: unknown): NormalizedPaginatedResult<T> {
  if (Array.isArray(raw)) {
    return {
      rows: raw as T[],
      total: raw.length,
    };
  }

  if (typeof raw === "object" && raw !== null) {
    const candidate = raw as {
      content?: unknown;
      data?: unknown;
      totalElements?: unknown;
      total?: unknown;
    };

    const rows = Array.isArray(candidate.content)
      ? (candidate.content as T[])
      : Array.isArray(candidate.data)
        ? (candidate.data as T[])
        : [];

    const total =
      typeof candidate.totalElements === "number"
        ? candidate.totalElements
        : typeof candidate.total === "number"
          ? candidate.total
          : rows.length;

    return { rows, total };
  }

  return { rows: [], total: 0 };
}

const compareText = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { sensitivity: "base" });

const getGroupNames = (account: PagAccountRow): string[] =>
  (account.groupNames ?? [])
    .map((group) => group?.trim())
    .filter((group): group is string => Boolean(group))
    .sort(compareText);

/**
 * Sorts account rows by group names alphabetically in ascending or descending order.
 * Handles accounts belonging to one or multiple groups by sorting their group names first.
 * Accounts without groups are placed consistently at the end.
 */
export const sortAccountsByGroup = (
  accounts: PagAccountRow[],
  direction: "asc" | "desc" = "asc",
): PagAccountRow[] => {
  const order = direction === "asc" ? 1 : -1;

  return [...accounts].sort((a, b) => {
    const groupsA = getGroupNames(a);
    const groupsB = getGroupNames(b);

    // Keep accounts without groups at the bottom.
    if (!groupsA.length || !groupsB.length) {
      if (!groupsA.length && !groupsB.length) {
        return order * compareText(
          a.accountName ?? "",
          b.accountName ?? "",
        );
      }

      return groupsA.length ? -1 : 1;
    }

    const groupComparison = compareText(
      groupsA.join(", "),
      groupsB.join(", "),
    );

    if (groupComparison !== 0) {
      return order * groupComparison;
    }

    // Use account name when group names are identical.
    return order * compareText(
      a.accountName ?? "",
      b.accountName ?? "",
    );
  });
};
